

import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import {
  HiHome, HiCollection, HiShoppingBag, HiClipboardList,
  HiUsers, HiTag, HiExclamation, HiChartBar,
  HiDownload, HiSearch, HiDocumentText, HiArrowLeft, HiLockClosed,
  HiX, HiTruck, HiMenu, HiChevronRight,  HiMail
} from 'react-icons/hi';

import AdminOverview from './admin/AdminOverview';
import AdminCategories from './admin/AdminCategories';
import AdminProducts from './admin/AdminProducts';
import AdminOrders from './admin/AdminOrders';
import AdminCustomers from './admin/AdminCustomers';
import AdminDiscounts from './admin/AdminDiscounts';
import AdminLowStock from './admin/AdminLowStock';
import AdminAbandonedCarts from './admin/AdminAbandonedCarts';
import AdminImportExport from './admin/AdminImportExport';
import AdminSEO from './admin/AdminSEO';
import AdminBlog from './admin/AdminBlog';
import AdminSiteLock from './admin/AdminSiteLock';
import AdminShipping from './admin/AdminShipping';
import AdminNewsletter from './admin/AdminNewsletter';

const navGroups = [
  {
    label: 'Dashboard',
    items: [
      { id: 'overview', label: 'Overview', icon: HiHome },
    ],
  },
  {
    label: 'Catalog',
    items: [
      { id: 'categories', label: 'Categories', icon: HiCollection },
      { id: 'products', label: 'Products', icon: HiShoppingBag },
    ],
  },
  {
    label: 'Sales',
    items: [
      { id: 'orders', label: 'Orders', icon: HiClipboardList },
      { id: 'customers', label: 'Customers', icon: HiUsers },
      { id: 'discounts', label: 'Discounts', icon: HiTag },
      { id: 'abandoned', label: 'Abandoned Carts', icon: HiChartBar },
      { id: 'newsletter', label: 'Newsletter', icon: HiMail  },
    ],
  },
  {
    label: 'Operations',
    items: [
      { id: 'shipping', label: 'Shipping', icon: HiTruck },
      { id: 'lowstock', label: 'Low Stock', icon: HiExclamation },
      { id: 'importexport', label: 'Import / Export', icon: HiDownload },
    ],
  },
  {
    label: 'Content & Settings',
    items: [
      { id: 'seo', label: 'SEO', icon: HiSearch },
      { id: 'blog', label: 'Blog', icon: HiDocumentText },
      { id: 'sitelock', label: 'Site Lock', icon: HiLockClosed },
    ],
  },
];

const tabLabels = navGroups
  .flatMap(g => g.items)
  .reduce((acc, item) => ({ ...acc, [item.id]: item.label }), {});

const tabComponents = {
  overview: AdminOverview,
  categories: AdminCategories,
  products: AdminProducts,
  orders: AdminOrders,
  customers: AdminCustomers,
  discounts: AdminDiscounts,
  lowstock: AdminLowStock,
  abandoned: AdminAbandonedCarts,
  importexport: AdminImportExport,
  seo: AdminSEO,
  blog: AdminBlog,
  sitelock: AdminSiteLock,
  shipping: AdminShipping,
  newsletter: AdminNewsletter,
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isAdmin = user?.role === 'ADMIN';
  if (!isAdmin) return <Navigate to="/" replace />;

  const ActiveComponent = tabComponents[activeTab] ?? AdminOverview;
  const activeLabel = tabLabels[activeTab] ?? 'Overview';

  const handleNav = (id) => {
    setActiveTab(id);
    setSidebarOpen(false);
  };

  return (
    <div style={styles.shell}>
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          style={styles.backdrop}
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* ── Sidebar ─────────────────────────────── */}
      <aside style={{ ...styles.sidebar, ...(sidebarOpen ? styles.sidebarOpen : {}) }}>
        {/* Logo row */}
        <div style={styles.sidebarHeader}>
          <Link to="/" style={styles.logo}>
            <span style={styles.logoDot} />
            WAVEYYENT
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            style={styles.closeBtn}
            aria-label="Close menu"
            className="lg-hide"
          >
            <HiX size={20} />
          </button>
        </div>

        {/* Nav groups */}
        <nav style={styles.nav} aria-label="Admin navigation">
          {navGroups.map((group) => (
            <div key={group.label} style={styles.navGroup}>
              <p style={styles.groupLabel}>{group.label}</p>
              {group.items.map(({ id, label, icon: Icon }) => {
                const active = activeTab === id;
                return (
                  <button
                    key={id}
                    onClick={() => handleNav(id)}
                    style={{
                      ...styles.navBtn,
                      ...(active ? styles.navBtnActive : {}),
                    }}
                    aria-current={active ? 'page' : undefined}
                  >
                    <span style={{ ...styles.navIcon, ...(active ? styles.navIconActive : {}) }}>
                      <Icon size={17} />
                    </span>
                    <span style={styles.navLabel}>{label}</span>
                    {active && (
                      <span style={styles.navPip} aria-hidden="true" />
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div style={styles.sidebarFooter}>
          <Link to="/products" style={styles.backLink}>
            <HiArrowLeft size={15} />
            Back to store
          </Link>
        </div>
      </aside>

      {/* ── Main ────────────────────────────────── */}
      <div style={styles.mainWrapper}>
        {/* Topbar (mobile + desktop) */}
        <header style={styles.topbar}>
          <button
            onClick={() => setSidebarOpen(true)}
            style={styles.menuBtn}
            aria-label="Open menu"
          >
            <HiMenu size={20} />
          </button>

          {/* Breadcrumb */}
          <div style={styles.breadcrumb}>
            <span style={styles.breadcrumbRoot}>Admin</span>
            <HiChevronRight size={14} style={{ color: '#94a3b8', flexShrink: 0 }} aria-hidden="true" />
            <span style={styles.breadcrumbCurrent}>{activeLabel}</span>
          </div>

          {/* Right slot — avatar */}
          <div style={styles.avatarWrap} title={user?.email ?? 'Admin'}>
            <span style={styles.avatarInitials}>
              {(user?.name ?? user?.email ?? 'A')[0].toUpperCase()}
            </span>
          </div>
        </header>

        {/* Page content */}
        <main style={styles.main}>
          <ActiveComponent />
        </main>
      </div>

      {/* Responsive overrides via a real <style> tag */}
      <style>{`
        @media (min-width: 1024px) {
          .lg-hide { display: none !important; }
        }
      `}</style>
    </div>
  );
}

/* ── Design tokens ──────────────────────────────────────────────────────── */
const SIDEBAR_W = 240;
const SIDEBAR_BG = '#0f172a';   /* slate-900 */
const ACTIVE_BG  = '#1e293b';   /* slate-800 */
const ACTIVE_ACC = '#6366f1';   /* indigo-500 */
const TEXT_MUT   = '#64748b';   /* slate-500 */
const TEXT_SEC   = '#94a3b8';   /* slate-400 */
const TEXT_PRI   = '#f1f5f9';   /* slate-100 */
const BORDER     = '#1e293b';

const styles = {
  shell: {
    display: 'flex',
    minHeight: '100vh',
    background: '#f8fafc',
    fontFamily: 'Inter, system-ui, sans-serif',
  },

  /* Backdrop */
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15,23,42,0.6)',
    backdropFilter: 'blur(2px)',
    zIndex: 40,
  },

  /* ── Sidebar ── */
  sidebar: {
    width: SIDEBAR_W,
    minWidth: SIDEBAR_W,
    background: SIDEBAR_BG,
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    zIndex: 50,
    transform: 'translateX(-100%)',
    transition: 'transform 0.25s cubic-bezier(0.4,0,0.2,1)',
    overflowY: 'auto',
    overflowX: 'hidden',

    /* Desktop: always visible */
    '@media (min-width: 1024px)': {
      position: 'relative',
      transform: 'none',
    },
  },
  sidebarOpen: {
    transform: 'translateX(0)',
  },

  sidebarHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 16px 16px',
    borderBottom: `1px solid ${BORDER}`,
    flexShrink: 0,
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontWeight: 700,
    fontSize: 15,
    letterSpacing: '0.06em',
    color: TEXT_PRI,
    textDecoration: 'none',
  },
  logoDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: ACTIVE_ACC,
    flexShrink: 0,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: TEXT_SEC,
    cursor: 'pointer',
    padding: 4,
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
  },

  nav: {
    flex: 1,
    padding: '12px 0',
    overflowY: 'auto',
  },
  navGroup: {
    marginBottom: 4,
    padding: '4px 0',
  },
  groupLabel: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: TEXT_MUT,
    padding: '8px 16px 4px',
    margin: 0,
  },
  navBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    padding: '9px 16px',
    background: 'none',
    border: 'none',
    borderRadius: 0,
    cursor: 'pointer',
    color: TEXT_SEC,
    fontSize: 13.5,
    fontWeight: 400,
    textAlign: 'left',
    transition: 'background 0.15s, color 0.15s',
    position: 'relative',
  },
  navBtnActive: {
    background: ACTIVE_BG,
    color: TEXT_PRI,
    fontWeight: 500,
  },
  navIcon: {
    display: 'flex',
    alignItems: 'center',
    opacity: 0.6,
    flexShrink: 0,
  },
  navIconActive: {
    opacity: 1,
    color: ACTIVE_ACC,
  },
  navLabel: {
    flex: 1,
  },
  navPip: {
    width: 3,
    height: 20,
    background: ACTIVE_ACC,
    borderRadius: '2px 0 0 2px',
    position: 'absolute',
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
  },

  sidebarFooter: {
    padding: '12px 16px 20px',
    borderTop: `1px solid ${BORDER}`,
    flexShrink: 0,
  },
  backLink: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    color: TEXT_MUT,
    textDecoration: 'none',
    transition: 'color 0.15s',
  },

  /* ── Main wrapper ── */
  mainWrapper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    /* Push content right of the sidebar on desktop */
    marginLeft: 0,
  },

  topbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    height: 56,
    padding: '0 20px',
    background: '#ffffff',
    borderBottom: '1px solid #e2e8f0',
    position: 'sticky',
    top: 0,
    zIndex: 30,
    flexShrink: 0,
  },
  menuBtn: {
    background: 'none',
    border: 'none',
    color: '#475569',
    cursor: 'pointer',
    padding: 6,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flex: 1,
    minWidth: 0,
  },
  breadcrumbRoot: {
    fontSize: 13,
    color: '#94a3b8',
    whiteSpace: 'nowrap',
  },
  breadcrumbCurrent: {
    fontSize: 14,
    fontWeight: 600,
    color: '#0f172a',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  avatarWrap: {
    width: 34,
    height: 34,
    borderRadius: '50%',
    background: '#eef2ff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    cursor: 'default',
  },
  avatarInitials: {
    fontSize: 13,
    fontWeight: 700,
    color: ACTIVE_ACC,
  },

  main: {
    flex: 1,
    padding: '24px 20px',
    maxWidth: 1200,
    width: '100%',
    margin: '0 auto',
    boxSizing: 'border-box',
  },
};